import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchBPlusSignal } from '../../api/QuantApi';
import AccessPasswordForm from '../../components/AccessPasswordForm';
import { setAccessPassword } from '../../utils/siteAccess';
import LineChart from './LineChart';
import translations from './translations';
import styles from './BPlusSignal.module.css';

const COLORS = {
    qqq: '#3b5bdb',
    tqqq: '#1f9d72',
    cash: '#d99a1c',
    close: '#172026',
    ema_pb: '#2a9d8f',
    sma50: '#e8590c',
    sma200: '#c92a2a',
    bear_line: '#e8838a',
    exit_line: '#5cc49c',
    bandTq: 'rgba(31, 157, 114, 0.10)',
    bandCash: 'rgba(201, 42, 42, 0.08)',
    perf: { bplus: '#1f9d72', b: '#3b5bdb', qqq: '#98a2b3', tqqq: '#e8590c' },
};

const RANGE_MONTHS = { '6m': 6, '1y': 12, '2y': 24, '3y': 36 };
// Backtest periods, in toggle order; keys match `backtests[].key` from the API.
const BACKTEST_WINDOWS = ['3y', '5y', '10y', 'all'];
const START_CODES = { pullback: ['pb_buy'], deep_rebound: ['deep_buy'], deep_watch: ['arm'], bear: ['bear_on'] };
const END_CODES = ['pb_exit', 'pb_stop', 'deep_exit', 'deep_stop', 'bear_off'];

const readPref = (key, fallback, allowed) => {
    try {
        const v = window.localStorage.getItem(key);
        return allowed.includes(v) ? v : fallback;
    } catch (e) {
        return fallback;
    }
};
const writePref = (key, value) => {
    try {
        window.localStorage.setItem(key, value);
    } catch (e) {
        // Private mode or blocked storage: the choice just isn't remembered.
    }
};

const pct = (v, digits = 1, sign = false) => {
    if (v == null || !Number.isFinite(v)) return '—';
    const s = `${(v * 100).toFixed(digits)}%`;
    return sign && v > 0 ? `+${s}` : s;
};
const usd = (v) => (v == null || !Number.isFinite(v) ? '—' : `$${v.toFixed(2)}`);
// Axis labels for growth of $1: whole dollars, or one decimal for ticks like $1.5.
const growthTick = (v) => `$${v >= 10 || Math.abs(v - Math.round(v)) < 1e-6 ? v.toFixed(0) : v.toFixed(1)}`;
const fmt = (str, vars = {}) => String(str).replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));

function Toggle({ options, value, onChange, label }) {
    return (
        <div className={styles.toggle} role="group" aria-label={label}>
            {options.map((o) => (
                <button
                    key={o.id}
                    type="button"
                    className={value === o.id ? styles.toggleActive : ''}
                    aria-pressed={value === o.id}
                    onClick={() => onChange(o.id)}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function AllocationBar({ weights, t }) {
    const parts = ['qqq', 'tqqq', 'cash'].filter((k) => (weights[k] || 0) > 0.001);
    return (
        <div>
            <div className={styles.allocBar}>
                {parts.map((k) => (
                    <div key={k} style={{ width: `${weights[k] * 100}%`, background: COLORS[k] }} />
                ))}
            </div>
            <div className={styles.allocLegend}>
                {['qqq', 'tqqq', 'cash'].map((k) => (
                    <div key={k} className={styles.allocItem}>
                        <span className={styles.swatch} style={{ background: COLORS[k] }} />
                        <span>{t.signal.assets[k]}</span>
                        <strong>{pct(weights[k] || 0, 0)}</strong>
                    </div>
                ))}
            </div>
        </div>
    );
}

function BPlusSignal() {
    const [lang, setLangState] = useState(() => readPref(
        'bplus.lang',
        (navigator.language || '').toLowerCase().startsWith('zh') ? 'zh' : 'en',
        ['en', 'zh'],
    ));
    const [version, setVersionState] = useState(() => readPref('bplus.version', 'bplus', ['bplus', 'b']));
    const [range, setRange] = useState('1y');
    const [btWindow, setBtWindowState] = useState(() => readPref('bplus.backtest', 'all', BACKTEST_WINDOWS));
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [locked, setLocked] = useState(false);

    const t = translations[lang];
    const setLang = (v) => { setLangState(v); writePref('bplus.lang', v); };
    const setVersion = (v) => { setVersionState(v); writePref('bplus.version', v); };
    const setBtWindow = (v) => { setBtWindowState(v); writePref('bplus.backtest', v); };

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        fetchBPlusSignal(version)
            .then((payload) => { setData(payload); setLocked(false); })
            .catch((e) => {
                // 401: no (or a wrong) site password yet — show the password box.
                if (e.response && e.response.status === 401) {
                    setData(null);
                    setLocked(true);
                } else {
                    setError(e);
                }
            })
            .finally(() => setLoading(false));
    }, [version]);

    const relock = () => {
        setAccessPassword('');
        setData(null);
        setLocked(true);
    };

    useEffect(() => { load(); }, [load]);

    const p = data && data.params;
    const vars = useMemo(() => (p ? {
        version: data.version_label,
        w: pct(p.pb_frac, 0),
        pb: pct(p.pb_frac, 0),
        ext: pct(p.pb_exit_ext, 0),
        dd: pct(p.bear_dd, 0),
        dd2: pct(p.ladder[1][0], 0),
        w1: pct(p.ladder[0][1], 0),
        w2: pct(p.ladder[1][1], 0),
        buf: pct(p.regime_buf, 0),
        base: pct(p.bear_base, 0),
        x: pct(p.exit_dd, 0),
    } : { version: version === 'b' ? 'B' : 'B+' }), [data, p, version]);

    const chartRows = useMemo(() => {
        if (!data || !data.chart) return [];
        const last = new Date(data.chart[data.chart.length - 1].date);
        const cut = new Date(last);
        cut.setMonth(cut.getMonth() - RANGE_MONTHS[range]);
        const iso = cut.toISOString().slice(0, 10);
        return data.chart.filter((r) => r.date >= iso);
    }, [data, range]);

    const bands = useMemo(() => {
        const out = [];
        const push = (test, color) => {
            let start = null;
            chartRows.forEach((r, i) => {
                if (test(r) && start == null) start = i;
                if ((!test(r) || i === chartRows.length - 1) && start != null) {
                    out.push({ from: start, to: test(r) ? i : i - 1, color });
                    start = null;
                }
            });
        };
        push((r) => (r.w_t || 0) > 0, COLORS.bandTq);
        push((r) => (r.w_c || 0) > 0, COLORS.bandCash);
        return out;
    }, [chartRows]);

    const eventText = (e) => {
        let code = e.code;
        if (code === 'pb_exit') code = p && p.pb_exit_mode === 'ext' ? 'pb_exit_ext' : 'pb_exit_newhigh';
        return fmt(t.events.codes[code] || code, { ...vars, v: pct(e.value, e.code === 'arm' ? 1 : 0) });
    };

    const sinceEvent = useMemo(() => {
        if (!data || !data.events) return null;
        const codes = START_CODES[data.state.code] || END_CODES;
        return data.events.find((e) => codes.includes(e.code)) || null;
    }, [data]);

    const header = (
        <header className={styles.header}>
            <div>
                <div className={styles.kicker}>{t.kicker}</div>
                <h1 className={styles.title}>{fmt(t.title, vars)}</h1>
                <p className={styles.subtitle}>{t.subtitle}</p>
                {data && data.available && (
                    <p className={styles.asOf}>
                        {fmt(t.asOf, { date: data.as_of })}
                        <span className={styles.dot}>·</span>
                        <Link to="/quantmonitor" className={styles.inlineLink}>{t.backToMonitor} →</Link>
                        <span className={styles.dot}>·</span>
                        <Link to="/forwardpe" className={styles.inlineLink}>{t.forwardPe} →</Link>
                    </p>
                )}
            </div>
            <div className={styles.controls}>
                <div className={styles.controlRow}>
                    <span>{t.version}</span>
                    <Toggle label={t.version} value={version} onChange={setVersion}
                        options={[{ id: 'bplus', label: 'B+' }, { id: 'b', label: 'B' }]} />
                </div>
                <div className={styles.controlRow}>
                    <span>{t.language}</span>
                    <Toggle label={t.language} value={lang} onChange={setLang}
                        options={[{ id: 'en', label: 'EN' }, { id: 'zh', label: '中文' }]} />
                </div>
                {data && data.available && (
                    <button type="button" className={styles.relock} onClick={relock}>{t.locked.relock}</button>
                )}
            </div>
        </header>
    );

    if (locked) {
        return (
            <div className={styles.page}><div className={styles.inner}>{header}
                <section className={`${styles.card} ${styles.lockCard}`} aria-labelledby="bplus-locked">
                    <h2 className={styles.h2} id="bplus-locked">{t.locked.title}</h2>
                    <p className={styles.note}>{t.locked.body}</p>
                    <AccessPasswordForm lang={lang} onUnlocked={load} />
                </section>
            </div></div>
        );
    }
    if (loading && !data) {
        return <div className={styles.page}><div className={styles.inner}>{header}<div className={styles.card}><p className={styles.muted}>{t.loading}</p></div></div></div>;
    }
    if (error && !data) {
        return (
            <div className={styles.page}><div className={styles.inner}>{header}
                <div className={styles.card}>
                    <p className={styles.errorText}>{t.error}</p>
                    <button type="button" className={styles.retry} onClick={load}>{t.retry}</button>
                </div>
            </div></div>
        );
    }
    if (data && !data.available) {
        return <div className={styles.page}><div className={styles.inner}>{header}<div className={styles.card}><p className={styles.muted}>{t.unavailable}</p></div></div></div>;
    }

    const state = t.states[data.state.code] || t.states.normal;
    const levelRows = [
        ['ema_pb', data.levels.ema_pb], ['sma50', data.levels.sma50], ['sma200', data.levels.sma200],
        ['bear_line', data.levels.bear_line], ['exit_line', data.levels.exit_line],
        ['hi252', data.levels.hi252], ['arm_line', data.levels.arm_line],
    ].filter(([, v]) => v != null);
    const close = data.price.close;
    const priceSeries = [
        { key: 'close', label: t.chart.series.close, color: COLORS.close, width: 1.8 },
        { key: 'ema_pb', label: t.chart.series.ema_pb, color: COLORS.ema_pb, width: 1.4 },
        { key: 'sma50', label: t.chart.series.sma50, color: COLORS.sma50, width: 1.2 },
        { key: 'sma200', label: t.chart.series.sma200, color: COLORS.sma200, width: 1.6 },
        { key: 'bear_line', label: t.chart.series.bear_line, color: COLORS.bear_line, width: 1, dash: '4 4' },
        ...(p.pb_exit_mode === 'ext'
            ? [{ key: 'exit_line', label: t.chart.series.exit_line, color: COLORS.exit_line, width: 1, dash: '4 4' }]
            : []),
    ];

    const backtests = data.backtests || [];
    const backtest = backtests.find((b) => b.key === btWindow) || backtests[0] || null;
    const fullBacktest = backtests.find((b) => b.key === 'all');
    const fullYears = fullBacktest
        ? Math.round((Date.parse(fullBacktest.end) - Date.parse(fullBacktest.start)) / (365.25 * 864e5))
        : null;

    return (
        <div className={styles.page}>
            <div className={styles.inner}>
                {header}

                {/* ---- Today's signal ---- */}
                <section className={`${styles.card} ${styles.signalCard}`} aria-labelledby="bplus-signal">
                    <div className={styles.signalMain}>
                        <div className={styles.cardLabel} id="bplus-signal">{t.signal.heading}</div>
                        <div className={`${styles.stateBadge} ${styles[`state_${data.state.code}`] || ''}`}>{state.name}</div>
                        <p className={styles.stateDesc}>{fmt(state.desc, vars)}</p>
                        {sinceEvent && (
                            <p className={styles.since}>{fmt(t.signal.since, { date: sinceEvent.date, price: usd(sinceEvent.close) })}</p>
                        )}
                        <div className={styles.cardLabel}>{t.signal.target}</div>
                        <AllocationBar weights={data.weights} t={t} />
                        <p className={styles.exposure}>{fmt(t.signal.exposure, { x: data.exposure.toFixed(2) })}</p>
                        <div className={styles.todayBox}>
                            {data.today_events.length === 0 ? (
                                <span className={styles.muted}>{t.signal.todayNone}</span>
                            ) : (
                                <>
                                    <strong>{t.signal.todayNew}</strong>
                                    <ul>{data.today_events.map((e) => <li key={e.code}>{eventText(e)}</li>)}</ul>
                                </>
                            )}
                        </div>
                    </div>
                    <div className={styles.signalStats}>
                        <div className={styles.stat}>
                            <div className={styles.statLabel}>{t.signal.close}</div>
                            <div className={styles.statValue}>{usd(close)}</div>
                            <div className={data.price.change >= 0 ? styles.pos : styles.neg}>{pct(data.price.change, 2, true)}</div>
                        </div>
                        <div className={styles.stat}>
                            <div className={styles.statLabel}>{t.signal.drawdown}</div>
                            <div className={styles.statValue}>{pct(data.metrics.drawdown_52w)}</div>
                        </div>
                        <div className={styles.stat}>
                            <div className={styles.statLabel}>{t.signal.ext200}</div>
                            <div className={styles.statValue}>{pct(data.metrics.ext200, 1, true)}</div>
                        </div>
                    </div>
                </section>

                {/* ---- Next triggers ---- */}
                <section className={styles.card} aria-labelledby="bplus-next">
                    <h2 className={styles.h2} id="bplus-next">{t.next.heading}</h2>
                    <p className={styles.note}>{t.next.note}</p>
                    <div className={styles.triggerGrid}>
                        {data.next_triggers.map((g) => (
                            <div key={g.code} className={`${styles.trigger} ${g.note ? styles.triggerMuted : ''}`}>
                                <div className={styles.triggerText}>{fmt(t.next.codes[g.code] || g.code, vars)}</div>
                                <div className={styles.triggerPrice}>
                                    <span className={styles.muted}>{g.direction === 'below' ? t.next.below : t.next.above}</span>
                                    <strong>{usd(g.price)}</strong>
                                    <span className={g.distance >= 0 ? styles.pos : styles.neg}>{pct(g.distance, 1, true)}</span>
                                </div>
                                {g.note && <div className={styles.triggerNote}>{t.next.notes[g.note]}</div>}
                            </div>
                        ))}
                    </div>
                </section>

                {/* ---- Chart ---- */}
                <section className={styles.card} aria-labelledby="bplus-chart">
                    <div className={styles.cardHead}>
                        <div>
                            <h2 className={styles.h2} id="bplus-chart">{t.chart.heading}</h2>
                            <p className={styles.note}>{t.chart.note}</p>
                        </div>
                        <Toggle label={t.chart.heading} value={range} onChange={setRange}
                            options={Object.keys(RANGE_MONTHS).map((k) => ({ id: k, label: t.chart.ranges[k] }))} />
                    </div>
                    <div className={styles.legend}>
                        {priceSeries.map((s) => (
                            <span key={s.key} className={styles.legendItem}>
                                <span className={styles.legendLine} style={{ borderTop: `2px ${s.dash ? 'dashed' : 'solid'} ${s.color}` }} />
                                {s.label}
                            </span>
                        ))}
                    </div>
                    <LineChart
                        data={chartRows}
                        series={priceSeries}
                        bands={bands}
                        height={360}
                        yFormat={(v, tip) => (tip ? usd(v) : `$${Math.round(v)}`)}
                        ariaLabel={t.chart.heading}
                        tooltipExtra={(r) => (
                            <div className={styles.tooltipFoot}>
                                {(r.w_t || 0) > 0 && <span style={{ color: COLORS.tqqq }}>{fmt(t.chart.tqqq, { w: pct(r.w_t, 0) })}</span>}
                                {(r.w_c || 0) > 0 && <span style={{ color: COLORS.cash }}>{fmt(t.chart.cash, { w: pct(r.w_c, 0) })}</span>}
                            </div>
                        )}
                    />
                </section>

                <div className={styles.twoCol}>
                    {/* ---- Key levels ---- */}
                    <section className={styles.card} aria-labelledby="bplus-levels">
                        <h2 className={styles.h2} id="bplus-levels">{t.levels.heading}</h2>
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead><tr><th>{t.levels.level}</th><th className={styles.num}>{t.levels.price}</th><th className={styles.num}>{t.levels.distance}</th></tr></thead>
                                <tbody>
                                    <tr className={styles.rowStrong}><td>{t.signal.close}</td><td className={styles.num}>{usd(close)}</td><td className={styles.num}>—</td></tr>
                                    {levelRows.map(([k, v]) => (
                                        <tr key={k}>
                                            <td>{fmt(t.levels.rows[k], vars)}</td>
                                            <td className={styles.num}>{usd(v)}</td>
                                            <td className={`${styles.num} ${v / close - 1 >= 0 ? styles.pos : styles.neg}`}>{pct(v / close - 1, 1, true)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* ---- Recent signals ---- */}
                    <section className={styles.card} aria-labelledby="bplus-events">
                        <h2 className={styles.h2} id="bplus-events">{t.events.heading}</h2>
                        <div className={`${styles.tableWrap} ${styles.tall}`}>
                            <table className={styles.table}>
                                <thead><tr><th>{t.events.date}</th><th>{t.events.signal}</th><th className={styles.num}>{t.events.close}</th></tr></thead>
                                <tbody>
                                    {data.events.map((e) => (
                                        <tr key={`${e.date}-${e.code}`}>
                                            <td className={styles.nowrap}>{e.date}</td>
                                            <td className={/stop/.test(e.code) ? styles.neg : ''}>{eventText(e)}</td>
                                            <td className={styles.num}>{usd(e.close)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>

                {/* ---- Backtest ---- */}
                {backtest && (
                    <section className={styles.card} aria-labelledby="bplus-perf">
                        <div className={styles.cardHead}>
                            <div>
                                <h2 className={styles.h2} id="bplus-perf">
                                    {backtest.years
                                        ? fmt(t.perf.headingWindow, { n: backtest.years, start: backtest.start })
                                        : fmt(t.perf.heading, { start: backtest.start })}
                                </h2>
                                <p className={styles.note}>{t.perf.note}</p>
                            </div>
                            <Toggle label={t.perf.window} value={backtest.key} onChange={setBtWindow}
                                options={BACKTEST_WINDOWS
                                    .filter((k) => backtests.some((b) => b.key === k))
                                    .map((k) => ({ id: k, label: fmt(t.perf.windows[k], { n: fullYears }) }))} />
                        </div>
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t.perf.strategy}</th>
                                        <th className={styles.num}>{t.perf.cagr}</th>
                                        <th className={styles.num}>{t.perf.maxdd}</th>
                                        <th className={styles.num}>{t.perf.sharpe}</th>
                                        <th className={styles.num}>{t.perf.ytd}</th>
                                        <th className={styles.num}>{t.perf.last12}</th>
                                        <th className={`${styles.num} ${styles.hideNarrow}`}>{t.perf.worst}</th>
                                        <th className={`${styles.num} ${styles.hideNarrow}`}>{t.perf.growth}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {backtest.rows.map((r) => (
                                        <tr key={r.key} className={r.key === data.version ? styles.rowStrong : ''}>
                                            <td><span className={styles.swatch} style={{ background: COLORS.perf[r.key] }} /> {t.perf.names[r.key]}</td>
                                            <td className={styles.num}>{pct(r.cagr)}</td>
                                            <td className={`${styles.num} ${styles.neg}`}>{pct(r.max_drawdown)}</td>
                                            <td className={styles.num}>{r.sharpe != null ? r.sharpe.toFixed(2) : '—'}</td>
                                            <td className={`${styles.num} ${r.ytd >= 0 ? styles.pos : styles.neg}`}>{pct(r.ytd, 1, true)}</td>
                                            <td className={`${styles.num} ${r.last_12m >= 0 ? styles.pos : styles.neg}`}>{pct(r.last_12m, 1, true)}</td>
                                            <td className={`${styles.num} ${styles.hideNarrow}`}>{pct(r.worst_year, 1, true)}</td>
                                            <td className={`${styles.num} ${styles.hideNarrow}`}>${r.growth_of_1 != null ? r.growth_of_1.toFixed(1) : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className={styles.cardLabel}>{t.perf.chartTitle}</div>
                        <LineChart
                            data={backtest.equity}
                            series={['qqq', 'tqqq', 'b', 'bplus'].map((k) => ({
                                key: k, label: t.perf.names[k], color: COLORS.perf[k], width: k === data.version ? 2.4 : 1.5,
                            }))}
                            log
                            height={300}
                            yFormat={(v, tip) => (tip ? `$${v.toFixed(2)}` : growthTick(v))}
                            xFormat={backtest.years && backtest.years <= 5 ? (d) => d.slice(0, 7) : (d) => d.slice(0, 4)}
                            ariaLabel={t.perf.chartTitle}
                        />
                    </section>
                )}

                {/* ---- Rules ---- */}
                <section className={styles.card} aria-labelledby="bplus-rules">
                    <h2 className={styles.h2} id="bplus-rules">{fmt(t.rules.heading, vars)}</h2>
                    <div className={styles.rulesGrid}>
                        {t.rules.items.map((item) => (
                            <div key={item.title} className={styles.rule}>
                                <h3>{item.title}</h3>
                                <p>{fmt(item.body, {
                                    ...vars,
                                    deepExit: p.exit_dd === 0 ? t.rules.deepExitNewHigh : fmt(t.rules.deepExitNear, vars),
                                    pbExit: p.pb_exit_mode === 'ext' ? fmt(t.rules.pbExitExt, vars) : t.rules.pbExitNewHigh,
                                })}</p>
                            </div>
                        ))}
                    </div>
                    <p className={styles.note}>{t.rules.other}</p>
                </section>

                <p className={styles.disclaimer}>{t.disclaimer}</p>
            </div>
        </div>
    );
}

export default BPlusSignal;
